import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { NAV_LINKS } from '../data/siteMap';

const socials = [
  { label: 'X', href: 'https://x.com/puremedify', icon: <svg className="h-7 w-7" fill="currentColor" viewBox="0 0 24 24"><path d="M22.46 5.92c-.77.35-1.6.59-2.47.7a4.3 4.3 0 0 0 1.88-2.37 8.59 8.59 0 0 1-2.72 1.04A4.28 4.28 0 0 0 16.11 4c-2.37 0-4.29 1.92-4.29 4.29 0 .34.04.67.11.99C7.69 9.13 4.07 7.38 1.64 4.7c-.37.64-.58 1.39-.58 2.19 0 1.51.77 2.84 1.95 3.62-.72-.02-1.4-.22-1.99-.55v.06c0 2.11 1.5 3.87 3.5 4.27-.36.1-.74.16-1.13.16-.28 0-.54-.03-.8-.08.54 1.7 2.12 2.94 3.99 2.97A8.6 8.6 0 0 1 2 19.54c-.29 0-.57-.02-.85-.05A12.13 12.13 0 0 0 8.29 21.5c7.55 0 11.68-6.26 11.68-11.68 0-.18-.01-.36-.02-.54.8-.58 1.5-1.3 2.05-2.12z" /></svg> },
  { label: 'Facebook', href: 'https://facebook.com/puremedify', icon: <svg className="h-7 w-7" fill="currentColor" viewBox="0 0 24 24"><path d="M22.675 0h-21.35C.595 0 0 .592 0 1.326v21.348C0 23.408.595 24 1.325 24h11.495v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.797.143v3.24l-1.918.001c-1.504 0-1.797.715-1.797 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116C23.406 24 24 23.408 24 22.674V1.326C24 .592 23.406 0 22.675 0" /></svg> },
  { label: 'Instagram', href: 'https://instagram.com/puremedify', icon: <svg className="h-7 w-7" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.334 3.608 1.308.974.974 1.246 2.241 1.308 3.608.058 1.266.07 1.646.07 4.85s-.012 3.584-.07 4.85c-.062 1.366-.334 2.633-1.308 3.608-.974.974-2.241 1.246-3.608 1.308-1.266.058-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.366-.062-2.633-.334-3.608-1.308-.974-.974-1.246-2.241-1.308-3.608C2.175 15.647 2.163 15.267 2.163 12s.012-3.584.07-4.85c.062-1.366.334-2.633 1.308-3.608.974-.974 2.241-1.246 3.608-1.308C8.416 2.175 8.796 2.163 12 2.163zm0-2.163C8.741 0 8.332.013 7.052.072 5.775.131 4.602.425 3.635 1.392 2.668 2.359 2.374 3.532 2.315 4.809.013 8.332 0 8.741 0 12c0 3.259.013 3.668.072 4.948.059 1.277.353 2.45 1.32 3.417.967.967 2.14 1.261 3.417 1.32C8.332 23.987 8.741 24 12 24s3.668-.013 4.948-.072c1.277-.059 2.45-.353 3.417-1.32.967-.967 1.261-2.14 1.32-3.417.059-1.28.072-1.689.072-4.948s-.013-3.668-.072-4.948c-.059-1.277-.353-2.45-1.32-3.417-.967-.967-2.14-1.261-3.417-1.32C15.668.013 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zm0 10.162a3.999 3.999 0 1 1 0-7.998 3.999 3.999 0 0 1 0 7.998zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" /></svg> },
];

interface FormStatus {
  success: boolean;
  message: string;
}

const quickLinks = NAV_LINKS.filter((link) => link.path !== '/contact' && link.path !== '/sitemap');

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '', honeypot: '', recaptcha: '' });
  const [status, setStatus] = useState<FormStatus | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRecaptcha = (value: string | null) => {
    setFormData({ ...formData, recaptcha: value || '' });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formData.honeypot) {
      setStatus({ success: false, message: 'Bot detected' });
      return;
    }
    if (!formData.recaptcha) {
      setStatus({ success: false, message: 'Please complete the reCAPTCHA' });
      return;
    }
    try {
      const response = await fetch('https://api.puremedify.com/contact_api.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ ...formData, 'g-recaptcha-response': formData.recaptcha }).toString(),
      });
      const result: FormStatus = await response.json();
      setStatus(result);
      if (result.success) {
        setFormData({ name: '', email: '', message: '', honeypot: '', recaptcha: '' });
      }
    } catch (error) {
      setStatus({ success: false, message: 'Error sending message' });
    }
  };

  useEffect(() => {
    if (status) {
      const timer = setTimeout(() => {
        setStatus(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [status]);

  return (
    <div className="flex min-h-screen w-full flex-col bg-gradient-to-br from-blue-50 to-teal-100">
      {/* Hero Section */}
      <section className="from-brand-light relative flex w-full flex-col items-center justify-center overflow-hidden bg-gradient-to-br to-blue-100 py-16 md:py-24">
        <div className="relative z-10 mx-auto max-w-3xl text-center">
          <h1 className="animate-fade-in mb-4 font-ubuntu text-4xl font-bold text-lochmara-blue drop-shadow-lg md:text-5xl">Contact Us</h1>
          <p className="animate-fade-in mb-6 font-redhat text-xl text-gray-700 delay-200 md:text-2xl">Let’s create a meaningful impact together. Whether you're a potential partner, client, or looking for support, our team is ready to listen.</p>
        </div>
        <svg className="animate-float-slow absolute left-10 top-10 z-0 h-24 w-24 opacity-30" viewBox="0 0 100 100" fill="none"><circle cx="50" cy="50" r="40" fill="#0087ca" /></svg>
        <svg className="animate-float-medium absolute right-20 top-24 z-0 h-16 w-16 opacity-20" viewBox="0 0 100 100" fill="none"><circle cx="50" cy="50" r="35" fill="#00b937" /></svg>
        <svg className="animate-float-fast absolute bottom-10 left-1/2 z-0 h-20 w-20 opacity-20" viewBox="0 0 100 100" fill="none"><circle cx="50" cy="50" r="30" fill="#004487" /></svg>
        <svg className="animate-float-cross absolute bottom-16 right-10 z-0 h-16 w-16" viewBox="0 0 64 64" fill="none"><rect x="28" y="8" width="8" height="48" rx="4" fill="#0087ca" /><rect x="8" y="28" width="48" height="8" rx="4" fill="#00b937" /></svg>
        <style>{`
          @keyframes fade-in { 0% { opacity: 0; transform: translateY(40px);} 100% { opacity: 1; transform: translateY(0);} }
          .animate-fade-in { animation: fade-in 1.2s cubic-bezier(.4,0,.2,1) both; }
          .delay-200 { animation-delay: 0.2s; }
          @keyframes float-slow { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-20px); } }
          .animate-float-slow { animation: float-slow 7s ease-in-out infinite; }
          @keyframes float-medium { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-30px); } }
          .animate-float-medium { animation: float-medium 5s ease-in-out infinite; }
          @keyframes float-fast { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-40px); } }
          .animate-float-fast { animation: float-fast 3.5s ease-in-out infinite; }
          @keyframes float-cross { 0%, 100% { transform: translateY(0) rotate(-8deg); } 50% { transform: translateY(-18px) rotate(8deg); } }
          .animate-float-cross { animation: float-cross 6s ease-in-out infinite; }
          @keyframes slide-in { 0% { opacity: 0; } 100% { opacity: 1; } }
          .animate-slide-in { animation: slide-in 0.3s ease-out forwards; }
        `}</style>
      </section>

      {/* Contact Form & Info */}
      <section className="flex w-full flex-col items-center px-4 py-12">
        <div className="flex w-full max-w-4xl flex-col gap-12 md:flex-row">
          {/* Contact Form */}
          <div className="relative">
            {status && (
              <div
                className={`animate-slide-in fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 max-w-md w-full p-4 rounded-lg shadow-lg cursor-pointer ${status.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
                onClick={() => setStatus(null)}
              >
                {status.message}
              </div>
            )}
            <form className="animate-fade-in flex flex-1 flex-col gap-6 rounded-2xl bg-white/60 p-10 shadow-2xl backdrop-blur-lg" onSubmit={handleSubmit}>
              <div className="relative">
                <input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  className="w-full rounded border border-gray-300 px-4 py-3 pl-12 text-lg focus:outline-none focus:ring-2 focus:ring-brand"
                  value={formData.name}
                  onChange={handleChange}
                />
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-brand"><svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5.121 17.804A13.937 13.937 0 0112 15c2.5 0 4.847.655 6.879 1.804M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg></span>
              </div>
              <div className="relative">
                <input
                  type="email"
                  name="email"
                  placeholder="Your Email"
                  className="w-full rounded border border-gray-300 px-4 py-3 pl-12 text-lg focus:outline-none focus:ring-2 focus:ring-brand"
                  value={formData.email}
                  onChange={handleChange}
                />
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-brand"><svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16 12H8m8 0a4 4 0 11-8 0 4 4 0 018 0zm-8 0V8a4 4 0 018 0v4" /></svg></span>
              </div>
              <div className="relative">
                <textarea
                  name="message"
                  placeholder="Your Message"
                  rows={5}
                  className="w-full rounded border border-gray-300 px-4 py-3 pl-12 text-lg focus:outline-none focus:ring-2 focus:ring-brand"
                  value={formData.message}
                  onChange={handleChange}
                />
                <span className="absolute left-4 top-6 text-brand"><svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 14h.01M16 10h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" /></svg></span>
              </div>
              <input
                type="text"
                name="honeypot"
                className="hidden"
                value={formData.honeypot}
                onChange={handleChange}
              />
              <ReCAPTCHA
                sitekey="6Lc5l7QrAAAAAPek5s5JEY3HFta6wJy8JPzy2toC"
                onChange={handleRecaptcha}
              />
              <button type="submit" className="rounded-full bg-brand py-3 text-lg font-bold text-white shadow-lg transition-transform duration-200 hover:scale-105 hover:bg-brand-green">Send Message</button>
            </form>
          </div>
          {/* Contact Info */}
          <div className="animate-fade-in flex flex-1 flex-col justify-center rounded-2xl bg-white/60 p-10 shadow-2xl backdrop-blur-lg delay-200">
            <h2 className="mb-4 text-xl font-bold text-lochmara-blue">Contact Information</h2>
            <ul className="mb-6 space-y-2 text-base text-gray-700">
              <li><b>Corporate Address:</b> Maneja House, 10 Mbato Road, Ngodo Isuochi, Umunneochi LGA, Abia State.</li>
              <li><b>Email:</b> <a href="mailto:info@puremedify.com" className="text-brand underline">info@puremedify.com</a></li>
              <li><b>Phone:</b> <a href="tel:+2347035855151" className="text-brand underline">+234 7035855151</a></li>
              <li><b>Website:</b> <a href="https://puremedify.com" className="text-brand underline">www.puremedify.com</a></li>
            </ul>
            <div className="mt-2 flex gap-6">
              {socials.map(s => (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" className="transform text-gray-400 transition hover:scale-125 hover:text-brand" aria-label={s.label}>
                  {s.icon}
                </a>
              ))}
            </div>

            <div className="mt-8 border-t border-gray-200 pt-6">
              <h3 className="mb-3 font-ubuntu text-lg font-bold text-lochmara-blue">Quick Links</h3>
              <nav className="flex flex-col gap-2">
                {quickLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="font-redhat text-base text-gray-600 transition hover:text-brand hover:underline"
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;